import { useNavigate } from 'react-router-dom';

const bullets = [
  'Reads your calendar — no setup per meeting',
  'Joins the bridge before the meeting starts',
  'Calls your phone directly and retries until you answer',
  'Bridges you in silently on mute — nobody knows you were deep in code',
  'Works across Zoom, Teams, Meet, Webex and any dial-in bridge',
];

export default function DialBackSpotlight() {
  const navigate = useNavigate();

  return (
    <section
      className="py-[110px] px-[60px] border-t border-[#1e1a18] bg-[#080808] max-md:px-6 max-md:py-20"
      style={{ fontFamily: "'Work Sans', sans-serif" }}
    >
      <div className="max-w-[1080px] mx-auto">
        {/* Label */}
        <span className="block text-[11px] font-bold tracking-[0.22em] text-[#d43742] uppercase mb-7">
          New Feature
        </span>

        {/* Main card */}
        <div className="grid grid-cols-2 gap-0 bg-[#0e0d0b] border border-[#2a2422] rounded-sm overflow-hidden max-md:grid-cols-1">

          {/* Left — copy */}
          <div className="py-14 px-12 flex flex-col justify-between max-md:px-8 max-md:py-10">
            <div>
              <div
                className="text-[11px] font-bold tracking-[0.18em] uppercase mb-5"
                style={{ color: '#c8472a', fontFamily: "'DM Mono', monospace" }}
              >
                dial-back · by Delegate
              </div>

              <h2
                className="font-black text-[#faf9f7] leading-[0.95] tracking-[-0.01em] mb-6"
                style={{
                  fontFamily: "'Big Shoulders Display', sans-serif",
                  fontSize: 'clamp(36px, 5vw, 64px)',
                }}
              >
                Missed another<br />
                meeting?<br />
                <span style={{ color: '#c8472a' }}>Not anymore.</span>
              </h2>

              <p className="text-[14px] font-light text-[#b8b2ac] leading-[1.75] max-w-[420px] mb-8">
                Flow state is a gift — but it doesn&apos;t respect your 10am standup.
                Jessica joins your conference bridge, holds your seat, and calls your
                phone directly until you answer. No notifications. A real phone call.
                Retrying until you surface.
              </p>

              {/* Bullets */}
              <ul className="space-y-3 mb-10">
                {bullets.map((b) => (
                  <li key={b} className="flex items-start gap-3 text-[13px] text-[#e0dbd5] leading-[1.5]">
                    <span className="mt-[3px] text-[#c8472a] flex-shrink-0">→</span>
                    {b}
                  </li>
                ))}
              </ul>
            </div>

            {/* CTA row */}
            <div className="flex items-center gap-5 flex-wrap">
              <button
                onClick={() => navigate('/dial-back')}
                className="inline-block px-8 py-4 bg-[#c8472a] text-[#f5f2ec] text-[12px] font-bold tracking-[0.14em] uppercase no-underline cursor-pointer transition-all duration-200 hover:bg-[#d43742] hover:-translate-y-[1px] whitespace-nowrap rounded-sm border-0"
              >
                Learn about Dial-Back
              </button>
              <button
                onClick={() => navigate('/dial-back#beta')}
                className="text-[12px] font-semibold tracking-[0.1em] text-[#908880] uppercase transition-colors duration-200 hover:text-[#faf9f7] whitespace-nowrap cursor-pointer bg-transparent border-0"
              >
                Join beta — $49/mo →
              </button>
            </div>
          </div>

          {/* Right — visual panel */}
          <div
            className="relative flex flex-col justify-end p-10 min-h-[420px] max-md:min-h-[280px] max-md:p-8"
            style={{
              background:
                'radial-gradient(ellipse 80% 70% at 60% 30%, rgba(200,71,42,0.12) 0%, transparent 65%), #0e0d0b',
            }}
          >
            {/* Decorative vertical line */}
            <div
              className="absolute top-0 left-0 w-[1px] h-full"
              style={{ background: 'linear-gradient(to bottom, transparent, #c8472a 30%, #c8472a 70%, transparent)', opacity: 0.3 }}
            />

            {/* Mock timeline card */}
            <div className="border border-white/[0.08] rounded-[3px] overflow-hidden mb-4">
              <div
                className="px-4 py-2 bg-white/[0.03] border-b border-white/[0.06] text-[#9a9488] text-[10px] tracking-[0.16em] uppercase"
                style={{ fontFamily: "'DM Mono', monospace" }}
              >
                Live — right now
              </div>
              {[
                { time: '10:00', text: 'Jessica joined your standup bridge', accent: false },
                { time: '10:01', text: 'Calling your phone…', accent: true },
                { time: '10:02', text: 'Retrying — no answer yet', accent: true },
                { time: '10:03', text: 'You answered. Bridging you in silently.', accent: false },
                { time: '10:03', text: 'You\'re in. On mute. Nobody noticed.', accent: false },
              ].map((row, i) => (
                <div
                  key={i}
                  className={`flex gap-4 px-4 py-3 border-b border-white/[0.04] last:border-b-0 ${
                    row.accent ? 'bg-[rgba(200,71,42,0.06)]' : ''
                  }`}
                >
                  <span
                    className="text-[#9a9488] min-w-[40px] text-[10px] pt-[2px]"
                    style={{ fontFamily: "'DM Mono', monospace" }}
                  >
                    {row.time}
                  </span>
                  <span
                    className={`text-[12px] leading-[1.5] ${row.accent ? 'text-[#c8472a]' : 'text-[rgba(245,242,236,0.7)]'}`}
                  >
                    {row.text}
                  </span>
                </div>
              ))}
            </div>

            {/* Price badge */}
            <div
              className="self-start px-4 py-2 border border-[#c8472a]/30 rounded-sm text-[11px] tracking-[0.1em] text-[#c8472a]"
              style={{ fontFamily: "'DM Mono', monospace" }}
            >
              Beta · $49/month · Unlimited dial-back
            </div>
          </div>
        </div>
      </div>
    </section>
  );
}
